import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { modelApi, type RelatedModelRunRow } from '../../api/modelApi';
import '../operations/RelatedOperationsDialog.css';

/**
 * The model runs beside one run, in one table — the model-run counterpart of
 * the operation pages' Related dialog, and drawn with the same styles.
 * See docs/FeatureDesigns/PipelineInfra/model-run-replay-target.md.
 */
const GROUP_LABELS: Record<RelatedModelRunRow['group'], string> = {
    'this-operation': 'This pipeline run',
    'same-submission': 'Other runs on the same submission or job',
    'replays': 'Admin re-runs with other models',
};

const GROUP_ORDER: RelatedModelRunRow['group'][] = ['this-operation', 'same-submission', 'replays'];

export function RelatedModelRunsDialog({ outputId, onClose }: { outputId: string; onClose: () => void }) {
    const navigate = useNavigate();
    const { data, isLoading, error } = useQuery({
        queryKey: ['relatedModelRuns', outputId],
        queryFn: () => modelApi.getRelatedModelRuns(outputId),
    });

    const groups = useMemo(() => GROUP_ORDER
        .map(g => ({ group: g, rows: (data ?? []).filter(r => r.group === g) }))
        .filter(g => g.rows.length > 0), [data]);

    const open = (row: RelatedModelRunRow) => {
        if (row.isCurrent) return;
        onClose();
        navigate(row.href);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content related-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Related model runs</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {isLoading && <div className="related-empty">Looking…</div>}
                    {error && <div className="related-empty">Couldn't load related runs: {(error as Error).message}</div>}
                    {!isLoading && !error && groups.every(g => g.rows.every(r => r.isCurrent)) && (
                        <div className="related-empty">No other model run is recorded beside this one.</div>
                    )}
                    {groups.map(({ group, rows }) => (
                        <section key={group} className="related-group">
                            <h3>{GROUP_LABELS[group]}</h3>
                            <table className="related-table">
                                <thead>
                                    <tr>
                                        <th>Prompt</th>
                                        <th>Time</th>
                                        <th>Reason</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr
                                            key={row.key}
                                            className={row.isCurrent ? 'is-current' : 'is-link'}
                                            onClick={() => open(row)}
                                        >
                                            <td className="related-op">
                                                {row.operation}
                                                {row.isCurrent && <span className="related-here">you are here</span>}
                                                {!row.isCurrent && (
                                                    <a
                                                        className="related-open"
                                                        href={row.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open in new tab"
                                                        aria-label={`Open ${row.operation} in new tab`}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                            <path d="M6.5 3.5H3.5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9.5" />
                                                            <path d="M9.5 2.5h4v4" />
                                                            <path d="M13.5 2.5 7.5 8.5" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </td>
                                            <td className="related-time">
                                                {row.at ? format(new Date(row.at), 'MMM d, HH:mm:ss') : '—'}
                                            </td>
                                            <td>{row.reason}</td>
                                            <td className="related-detail">{row.detail || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
